// =====================================================
// FOOTNOTES AND ENDNOTES in the redline: the notes the body's marks refer to, and the notes whose
// text changed, redlined.
// =====================================================

import { tagCrossRelIds } from './xml-helpers';
import { countFormatChanges } from './docx-tracking';
import { computeWordDiff } from './diff-engine-core';
import { parseParagraphXml } from './parsers-core';
import type { ParsedDocument, DiffEntry, DiffParagraph, DiffResult, RichParagraph } from '../types';
import { placeInline } from './inline';
import { diffPieces, sideOf } from './pieces';
import { NOTE_TEXT_NORM, fragmentText, withoutPageNumbers, noteParas, STRUCTURAL_PARA } from './story-text';
import { redlineParaSequence } from './story-redline';
import { addRelationship, declarePart, REL, CONTENT_TYPE } from './package-parts';

/** Map of note id -> full definition XML for a footnotes/endnotes part. */
export function noteDefs(notesXml: string, itemTag: string): Map<string, string> {
  const map = new Map<string, string>();
  const re = new RegExp(`<${itemTag}\\b[^>]*?\\bw:id="(-?\\d+)"[\\s\\S]*?</${itemTag}>`, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(notesXml)) !== null) if (!map.has(m[1])) map.set(m[1], m[0]);
  return map;
}

/** A note's text, as the notes are compared. */
const noteText = (def: string) => NOTE_TEXT_NORM(fragmentText(def));

/**
 * Each revised note paired with the original note it is, as the body comparison saw their marks: a
 * mark both drafts have in the same place (an unchanged paragraph's, or one an edited paragraph
 * keeps: inline.ts) names the same note in both, whatever its number. A draft's notes renumber when
 * one is added or removed before them, so note 3 of the revised draft can be note 4 of the
 * original. A note whose mark the comparison did not place (one in an edited table row, say) pairs
 * by number, unless that number's mark was added or removed. Returns revised id -> original id.
 */
export function notePairs(paragraphs: DiffParagraph[], noteType: 'footnote' | 'endnote'): { pairs: Map<string, string>; placedRev: Set<string>; placedOrig: Set<string> } {
  const re = new RegExp(`<w:${noteType}Reference\\b[^>]*?\\bw:id="(\\d+)"`);
  const idOf = (xml: string | undefined) => (xml && (xml.match(re) || [])[1]) || null;
  /** the notes a paragraph's marks name, in order: the parser's events, else its XML (accepted) */
  const marksOf = (rich: RichParagraph): string[] => rich.inline
    ? rich.inline.map(e => idOf(e.xml)).filter((x): x is string => !!x)
    : [...(rich.rawXml || '').replace(/<w:del\b[\s\S]*?<\/w:del>/g, '').matchAll(new RegExp(re.source, 'g'))].map(m => m[1]);
  const pairs = new Map<string, string>();
  const placedRev = new Set<string>(), placedOrig = new Set<string>();
  const inOrder = (o: string[], r: string[]) => {
    for (let k = 0; k < Math.min(o.length, r.length); k++) pairs.set(r[k], o[k]);
    for (const id of o) placedOrig.add(id);
    for (const id of r) placedRev.add(id);
  };
  for (const p of paragraphs) {
    if (p.type === 'equal') {
      if (p.origRich && p.revRich) inOrder(marksOf(p.origRich), marksOf(p.revRich));
      else for (const rich of [p.origRich, p.revRich]) if (rich) for (const id of marksOf(rich)) (rich === p.origRich ? placedOrig : placedRev).add(id);
      continue;
    }
    const o = p.origRich ? marksOf(p.origRich) : [], r = p.revRich ? marksOf(p.revRich) : [];
    if (p.type === 'modify' && p.origRich && p.revRich && o.length && r.length) {
      const isRow = !!(p.origRich.isTableRow || p.revRich.isTableRow);
      const placed = !isRow && p.diff ? placeInline(diffPieces(p.diff, sideOf(p.origRich), sideOf(p.revRich)),
        { inline: p.origRich.inline, words: (p.origRich.richTokens || []).length },
        { inline: p.revRich.inline, words: (p.revRich.richTokens || []).length }) : null;
      if (placed) {
        for (const e of placed) {
          const rid = e.fromOrig ? null : idOf(e.ev.xml), oid = e.fromOrig ? idOf(e.ev.xml) : idOf(e.origEv?.xml);
          if (rid) placedRev.add(rid);
          if (oid) placedOrig.add(oid);
          if (e.status === 'equal' && rid && oid) pairs.set(rid, oid);
        }
        continue;
      }
      // a row edited cell by cell: its marks in order when both drafts have as many; else not placed
      if (o.length === r.length) { inOrder(o, r); continue; }
      continue;
    }
    // a paragraph added, removed or moved whole: its marks added or removed
    for (const id of o) placedOrig.add(id);
    for (const id of r) placedRev.add(id);
  }
  return { pairs, placedRev, placedOrig };
}

/**
 * The notes whose text differs between the drafts: revised id -> the original id it is (notePairs,
 * when the comparison is given; else by number, as without it).
 */
export async function changedNotes(origDocData: ParsedDocument, revDocData: ParsedDocument, noteType: 'footnote' | 'endnote', diff?: DiffResult): Promise<Map<string, string>> {
  const changed = new Map<string, string>();
  const origZip = origDocData && origDocData.zip, revZip = revDocData && revDocData.zip;
  if (!origZip || !revZip) return changed;
  const partName = `word/${noteType}s.xml`, itemTag = `w:${noteType}`;
  const [o, r] = await Promise.all([origZip.file(partName)?.async('string'), revZip.file(partName)?.async('string')]);
  if (!o || !r) return changed;
  const od = noteDefs(o, itemTag), rd = noteDefs(r, itemTag);
  const { pairs, placedRev, placedOrig } = diff ? notePairs(diff.paragraphs, noteType)
    : { pairs: new Map<string, string>(), placedRev: new Set<string>(), placedOrig: new Set<string>() };
  // what the comparison did not place pairs by number
  const paired = new Set(pairs.values());
  for (const id of rd.keys()) {
    if (pairs.has(id) || placedRev.has(id) || placedOrig.has(id) || paired.has(id) || !od.has(id)) continue;
    pairs.set(id, id);
  }
  for (const [revId, origId] of pairs) {
    const oDef = od.get(origId), rDef = rd.get(revId);
    if (!oDef || !rDef || Number(revId) < 1) continue;
    if (noteText(oDef) !== noteText(rDef)) changed.set(revId, origId);
  }
  return changed;
}

/** A note's word diff as its redline writes it (rebuildNoteDef → redlineParaSequence →
 *  redlineOnePara): paragraph by paragraph, in order, unchanged paragraphs left out, a paragraph
 *  boundary between the rest (so a change in one paragraph and one in the next count apart),
 *  and how many tracked format changes the writer will record. The summary page counts these
 *  before the notes are written. */
export function noteDiff(oDef: string, rDef: string): { diff: DiffEntry[]; formats: number } {
  const rParas = noteParas(rDef), oParas = noteParas(oDef);
  const diff: DiffEntry[] = [];
  let formats = 0;
  for (let i = 0; i < Math.max(rParas.length, oParas.length); i++) {
    const rp = rParas[i], op = oParas[i];
    if (rp && op && NOTE_TEXT_NORM(fragmentText(withoutPageNumbers(rp))) === NOTE_TEXT_NORM(fragmentText(withoutPageNumbers(op)))) continue;
    if (diff.length) diff.push({ type: 'equal', value: '' });
    const r = rp ? parseParagraphXml(rp) : null, o = op ? parseParagraphXml(op) : null;
    const d = computeWordDiff(o ? o.text : '', r ? r.text : '');
    if (r && o && !STRUCTURAL_PARA.test(rp) && !STRUCTURAL_PARA.test(op)) formats += countFormatChanges(d, o, r);
    diff.push(...d);
  }
  return { diff, formats };
}

/**
 * Rewrite a changed note definition with a word-level redline of its body:
 * paragraphs paired by index; the rev paragraph's pPr and (first paragraph)
 * footnoteRef/endnoteRef run are preserved; added/removed words are emitted via
 * buildRevisionRun, so track-changes and markup modes both render correctly.
 * Structural bodies (tables, text boxes, drawings) are left verbatim — a wrong
 * redline is worse than none there.
 */

export const NOTE_REF_RUN_RE = /<w:r\b[^>]*>(?:(?!<\/w:r>)[\s\S])*?<w:(?:footnoteRef|endnoteRef)\s*\/>(?:(?!<\/w:r>)[\s\S])*?<\/w:r>/;

export function rebuildNoteDef(revDef: string, origDef: string): string | null {
  if (/<w:(?:tbl|txbxContent|drawing|pict|object)\b/.test(revDef + origDef)) return null;
  const rootOpen = (revDef.match(/^<w:(?:foot|end)note\b[^>]*>/) || [])[0];
  const rootClose = revDef.startsWith('<w:footnote') ? '</w:footnote>' : '</w:endnote>';
  if (!rootOpen) return null;
  const rParas = noteParas(revDef), oParas = noteParas(origDef);
  if (!rParas.length) return null;
  const inner = redlineParaSequence(rParas, oParas, NOTE_REF_RUN_RE);
  if (inner === null) return null;
  return rootOpen + inner + rootClose;
}

/** A note definition under another id, without its paragraphs' w14:paraId / textId (those must be
 *  unique per paragraph: a copy drops them). */
const noteUnder = (def: string, id: string) => def.replace(/^(<w:(?:foot|end)note\b[^>]*?\bw:id=")-?\d+"/, `$1${id}"`)
  .replace(/\s+w14:(?:paraId|textId)="[0-9A-Fa-f]+"/g, '');
const emptyNote = (itemTag: string, id: string) => `<${itemTag} w:id="${id}"><w:p><w:r><w:t/></w:r></w:p></${itemTag}>`;

/**
 * Every footnote and endnote mark in the body, given the note it means, in one place (ids here are
 * note ids, a 16-bit space of their own, counted from the highest the body and the output's notes
 * use):
 *
 * 1. A mark from the original (tagged FRO-N as it was written: a deleted paragraph's, or one an
 *    edited paragraph deletes) keeps N when the output's note N reads as the original's note N, or
 *    when the output has no note N. Otherwise the revised draft uses N for another note (its notes
 *    renumber when one is added or removed before it) or edited it: the mark gets an id of its own,
 *    with the original's note copied under it, so Reject All shows the original's note.
 * 2. A mark whose note the output does not have gets the original's note of that id (or an empty
 *    one). When only the original had notes, the output gets a notes part, the original's.
 * 3. A note marked twice (the same note on both sides of an edit) keeps its id at its first mark;
 *    each later mark gets an id of its own and a copy of the note: Word needs one mark per note.
 */
export async function resolveNoteRefs(newZip: any, origZip: any, documentXml: string): Promise<string> {
  for (const noteType of ['footnote', 'endnote'] as const) {
    const refRe = () => new RegExp(`(<w:${noteType}Reference\\b[^>]*?\\bw:id=")(FRO-)?(\\d+)"`, 'g');
    if (!refRe().test(documentXml)) continue;
    const partName = `word/${noteType}s.xml`, itemTag = `w:${noteType}`, rootClose = `</w:${noteType}s>`;
    const origXml: string | undefined = origZip ? await origZip.file(partName)?.async('string') : undefined;
    let notesXml: string | undefined = await newZip.file(partName)?.async('string');
    if (notesXml == null) {
      // the revised draft has no notes: its marks have none to point at; the original's bring theirs
      if (origXml == null) { documentXml = documentXml.replace(refRe(), '$1$3"'); continue; }
      notesXml = tagCrossRelIds(origXml);
      await declarePart(newZip, partName, CONTENT_TYPE[`${noteType}s`]);
      const rels: string | undefined = await newZip.file('word/_rels/document.xml.rels')?.async('string');
      const target = partName.replace('word/', '');
      if (rels && !new RegExp(`Target="${target}"`).test(rels)) await addRelationship(newZip, 'word/_rels/document.xml.rels', { type: REL[`${noteType}s`], target });
    }
    const outDefs = noteDefs(notesXml, itemTag), origDefs = origXml != null ? noteDefs(origXml, itemTag) : new Map<string, string>();

    // 1. the original's marks
    let maxId = 0;
    for (const m of documentXml.matchAll(refRe())) maxId = Math.max(maxId, +m[3]);
    for (const id of outDefs.keys()) maxId = Math.max(maxId, +id || 0);
    const own = new Map<string, string>();   // the original's note id -> its id in the output
    let copied = '';
    documentXml = documentXml.replace(refRe(), (whole: string, pre: string, tag: string | undefined, id: string) => {
      if (!tag) return whole;
      const od = origDefs.get(id), cur = outDefs.get(id);
      if (!od || !cur || noteText(od) === noteText(cur)) return `${pre}${id}"`;
      if (!own.has(id)) {
        const nid = String(++maxId);
        own.set(id, nid);
        copied += noteUnder(tagCrossRelIds(od), nid);
      }
      return `${pre}${own.get(id)}"`;
    });

    // 2. marks whose note the output does not have
    const defined = new Set([...outDefs.keys(), ...own.values()]);
    let missing = '';
    for (const m of documentXml.matchAll(refRe())) {
      const id = m[3];
      if (defined.has(id)) continue;
      defined.add(id);
      const od = origDefs.get(id);
      missing += od ? tagCrossRelIds(od) : emptyNote(itemTag, id);
    }

    // 3. a note marked twice
    let all = notesXml.replace(rootClose, copied + missing + rootClose);
    for (const id of noteDefs(all, itemTag).keys()) maxId = Math.max(maxId, +id || 0);
    const seen = new Set<string>();
    let twice = '';
    documentXml = documentXml.replace(refRe(), (whole: string, pre: string, _tag: string | undefined, id: string) => {
      if (!seen.has(id)) { seen.add(id); return whole; }
      const nid = String(++maxId);
      const def = noteDefs(all, itemTag).get(id);
      twice += def ? noteUnder(def, nid) : emptyNote(itemTag, nid);
      return `${pre}${nid}"`;
    });
    if (twice) all = all.replace(rootClose, twice + rootClose);
    if (all !== (await newZip.file(partName)?.async('string'))) newZip.file(partName, all);
  }
  return documentXml;
}

export async function redlineChangedNotes(newZip: any, origDocData: ParsedDocument, hasDocxBase: boolean, changedFootnotes: Map<string, string>, changedEndnotes: Map<string, string>): Promise<void> {
  // ===== REDLINE CHANGED FOOTNOTE / ENDNOTE BODIES =====
  // The body diff never sees note bodies; an edit inside one used to ship
  // silently unmarked. Rewrite each changed definition (detected against the
  // source docs above) with a word-level redline — Word renders it in the
  // footnote pane like any other tracked change.
  if (hasDocxBase && origDocData.zip) {
    for (const [partName, itemTag, changed] of [
      ['word/footnotes.xml', 'w:footnote', changedFootnotes],
      ['word/endnotes.xml', 'w:endnote', changedEndnotes],
    ] as const) {
      if (!changed.size) continue;
      const partFile = newZip.file(partName);
      const origPart = origDocData.zip.file(partName);
      if (!partFile || !origPart) continue;
      let notesXml: string = await partFile.async('string');
      const origDefsMap = noteDefs(await origPart.async('string'), itemTag);
      const outDefsMap = noteDefs(notesXml, itemTag);
      let touched = false;
      for (const [revId, origId] of changed) {
        const revDef = outDefsMap.get(revId);
        const origDef = origDefsMap.get(origId);
        if (!revDef || !origDef) continue;
        const rebuilt = rebuildNoteDef(revDef, origDef);
        if (rebuilt && rebuilt !== revDef) { notesXml = notesXml.replace(revDef, rebuilt); touched = true; }
      }
      if (touched) newZip.file(partName, notesXml);
    }
  }
}
